import type { PublicConfig } from "../../domain/app-types.ts";
import { authView } from "./view.ts";
import { bindPasswordVisibility } from "../../ui/password-visibility.ts";

export interface AuthControllerCapabilities {
  registrationMode: PublicConfig["registration_mode"];
  request(path: string, init?: RequestInit): Promise<Response>;
  apiError(response: Response, fallback: string): Promise<string>;
  promptEmail(): Promise<string | null>;
  markSignedIn(): Promise<void>;
  render(message?: string): void;
  resetToken(): string | null;
  clearResetToken(): void;
  reload(): void;
  icon(name: string): string;
  logo(): string;
}

export interface AuthWorkflowCapabilities extends Omit<AuthControllerCapabilities, "registrationMode" | "render"> {
  registrationMode(): PublicConfig["registration_mode"];
}

export function createAuthWorkflowController(root: HTMLElement, capabilities: AuthWorkflowCapabilities) {
  function render(message = ""): void {
    const { registrationMode, ...stable } = capabilities;
    bindAuth(root, message, { ...stable, registrationMode: registrationMode(), render });
  }
  return { render };
}

export function bindAuth(root: HTMLElement, message: string, capabilities: AuthControllerCapabilities): void {
  root.ownerDocument.title = "WeirDo — Local-first todos";
  root.innerHTML = authView({ registrationMode: capabilities.registrationMode, message, icon: capabilities.icon, logo: capabilities.logo });
  const form = root.querySelector<HTMLFormElement>("#auth-form")!;
  let mode: "login" | "register" | "reset" = capabilities.resetToken() ? "reset" : "login";
  let busy = false;
  const feedback = (text: string, error = false) => {
    const notice = root.querySelector<HTMLElement>("[data-auth-feedback]")!;
    notice.textContent = text; notice.hidden = !text; notice.classList.toggle("notice--error", error); notice.setAttribute("role", error ? "alert" : "status");
  };
  const updateMode = () => {
    root.querySelector("#auth-title")!.textContent = mode === "register" ? "Create your space" : mode === "reset" ? "Choose a new password" : "Sign in to your space";
    form.querySelector<HTMLButtonElement>('[type="submit"]')!.textContent = mode === "register" ? "Create account" : mode === "reset" ? "Reset password" : "Sign in";
    const password = form.querySelector<HTMLInputElement>('[name="password"]');
    if (password) password.autocomplete = mode === "register" ? "new-password" : "current-password";
    const code = root.querySelector<HTMLLabelElement>(".registration-code");
    if (code) { code.hidden = mode !== "register"; code.querySelector("input")!.disabled = mode !== "register"; const toggle = code.querySelector<HTMLButtonElement>(".password-visibility"); if (toggle) toggle.disabled = mode !== "register"; }
    const register = root.querySelector<HTMLElement>("#register");
    if (register) register.textContent = mode === "register" ? "Sign in instead" : capabilities.registrationMode === "allowlist" ? "Create an approved account" : "Create an account";
  };
  if (mode === "reset") {
    root.querySelector("#register")?.closest(".auth-switch")?.remove(); root.querySelector("#forgot-password")?.closest(".auth-switch")?.remove();
    form.innerHTML = '<label>New password<input name="new_password" type="password" autocomplete="new-password" minlength="10" required placeholder="At least 10 characters"></label><button class="button button--primary button--wide" type="submit">Reset password</button>';
  }
  bindPasswordVisibility(form); updateMode();
  const run = async (operation: () => Promise<void>) => {
    if (busy) return; busy = true;
    const controls = [...root.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input,button")].filter(control => !control.disabled);
    form.setAttribute("aria-busy", "true"); controls.forEach(control => { control.disabled = true; });
    try { await operation(); } catch { feedback("The server is unavailable. Your entries are still here; please try again.", true); }
    finally { busy = false; form.removeAttribute("aria-busy"); controls.forEach(control => { control.disabled = false; }); if (root.querySelector("#auth-form") === form) updateMode(); }
  };
  root.querySelector("#register")?.addEventListener("click", () => { if (busy) return; mode = mode === "login" ? "register" : "login"; feedback(""); updateMode(); });
  root.querySelector("#forgot-password")?.addEventListener("click", () => { void run(async () => {
    const email = await capabilities.promptEmail(); if (!email) return;
    const response = await capabilities.request("/api/v1/auth/password/reset/request", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ email }) });
    if (!response.ok) { feedback("Password recovery is unavailable right now. Please try again later.", true); return; }
    feedback("If that account exists, password-reset instructions have been sent.");
  }); });
  form.addEventListener("submit", event => {
    event.preventDefault(); if (busy) return;
    const data = Object.fromEntries(new (root.ownerDocument.defaultView!.FormData)(form));
    const operationMode = mode;
    const token = capabilities.resetToken();
    void run(async () => {
      const response = await capabilities.request(operationMode === "reset" ? "/api/v1/auth/password/reset" : `/api/v1/auth/${operationMode}`, { method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify(operationMode === "reset" ? { token, new_password: data.new_password } : data) });
      if (!response.ok) { feedback(await capabilities.apiError(response, operationMode === "reset" ? "Could not reset password" : "Could not sign in. Check your entries and try again."), true); return; }
      if (operationMode === "reset") { capabilities.clearResetToken(); capabilities.render(); root.querySelector<HTMLInputElement>('[name="email"]')?.focus(); const notice = root.querySelector<HTMLElement>("[data-auth-feedback]"); if (notice) { notice.hidden = false; notice.classList.remove("notice--error"); notice.setAttribute("role", "status"); notice.textContent = "Password changed. Sign in with your new password."; } return; }
      try { await capabilities.markSignedIn(); capabilities.reload(); }
      catch { feedback("Signed in, but this device could not finish loading. Reload the page to retry.", true); }
    });
  });
}
